// app/utilities/specHelpers.ts
import type { SpecCategory, VariantItem } from "@/app/utilities/interfaces"; // adjust path
import { getBrands, getModels, getPopularComparisons, ComparisonListQueryParams } from "./apis";
import { BODY_TYPES, BUDGET_PAGE_PRICE_RANGES, FUEL_TYPES, monthString, SEATING_CAPACITY, TRANSMISSIONS, yearString } from "./constants";

// Generate compare page title and intro paragraph for a list of variants
import type { Variant } from "./compareTypes";
import { formatCompareHeading } from "./slug";
import { convertModelTags, findSpecValue } from "./modelTagConverter";
import type { ListingSearchQueries, ModelWithImages } from "./types";

// Resolves the best available image for a model: featured image, then image,
// then the first entry of the images array (string or { url } shape).
export const getFirstModelImage = (model?: ModelWithImages | null): string => {
	const firstImage = model?.images?.[0];
	const firstImageUrl = typeof firstImage === "string" ? firstImage : firstImage?.url;
	return model?.featured_image || model?.image || firstImageUrl || "";
};

type ListingOgFilterExtras =
	| Record<string, unknown>
	| ((parsed: ReturnType<typeof parseFilterParams>) => Record<string, unknown>);

// Fetches the first listing model for OG/schema images using shared filter + image rules.
export const getListingOgImage = async (
	searchQueries?: ListingSearchQueries,
	extraFilter: ListingOgFilterExtras = {},
	fallback = "",
): Promise<string> => {
	const parsed = parseFilterParams(searchQueries);
	const extras = typeof extraFilter === "function" ? extraFilter(parsed) : extraFilter;
	const {
		minPrice,
		maxPrice,
		bodyTypes,
		fuelTypes,
		transmissions,
		brands,
		seatingCapacities,
		mileages,
		ranges,
		batteryCapacities,
		sort,
	} = parsed;

	const modelsRes = await getModels({
		page: 1,
		limit: 1,
		body_type: bodyTypes,
		fuel_type: fuelTypes,
		transmission: transmissions,
		seating_capacity: seatingCapacities,
		mileage: mileages,
		range: ranges,
		battery_capacity: batteryCapacities,
		sort,
		brand: brands,
		...(minPrice && { min_price: minPrice, max_price: maxPrice }),
		...extras,
	});

	return getFirstModelImage(modelsRes?.data?.[0]) || fallback;
};

export const getOgSchemaImage = (
	models: Array<ModelWithImages | null | undefined> = [],
	fallback = "",
): string => {
	for (const model of models) {
		const image = getFirstModelImage(model);
		if (image) return image;
	}
	return fallback;
};

// Single definition of model lifecycle status, instead of inline string comparisons.
export const isDiscontinuedModel = (m: { status?: string | null } | null | undefined): boolean =>
   m?.status === 'discontinued';

export const isUpcomingModel = (m: { status?: string | null } | null | undefined): boolean =>
   m?.status === 'upcoming';

// Active: neither upcoming nor discontinued — i.e. currently purchasable.
export const isAvailableModel = (m: { status?: string | null } | null | undefined): boolean =>
   !isDiscontinuedModel(m) && !isUpcomingModel(m);

// Unwraps a Promise.allSettled result, logging and falling back on rejection
// instead of letting one failed call take down the whole page.
export function settled<T>(result: PromiseSettledResult<T>, fallback: T, label?: string): T {
	if (result.status === "fulfilled") return result.value;
	if (label) console.error(`Failed to fetch ${label}:`, result.reason);
	return fallback;
}

// Helper to get value and unit for large prices
const getUnitAndValue = (price: number | null | undefined) => {
	const safePrice = typeof price === "number" && !isNaN(price) ? price : 0;
	if (safePrice >= 10000000) {
		return { value: (Math.floor(safePrice / 100000) / 100).toFixed(2), unit: "Crore" };
	} else if (safePrice >= 100000) {
		return { value: (Math.floor(safePrice / 1000) / 100).toFixed(2), unit: "Lakh" };
	} else {
		// For amounts below 1 Lakh, return the full rupee amount without unit,
		// formatted with no decimal places (e.g., 77000 -> "77,000").
		return { value: new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(safePrice)), unit: "" };
	}
};

// Format price range for display
export const formatPriceRange = (priceRange: { base: number; top: number | null; } | null | undefined, returnHtml = false): string => {
	if (!priceRange) return "Price to be Announced";
	const { base, top } = priceRange;

	if ((!base || base === 0) && (!top || top === 0)) return "Price to be Announced";

	const basePrice = getUnitAndValue(base);

	if (!top) {
		// Only base price
		return basePrice.unit ? `₹ ${basePrice.value} ${basePrice.unit}` : `₹ ${basePrice.value}`;
	}

	const topPrice = getUnitAndValue(top);

	// If both prices are numerically equal, show a single value instead of a range
	if (Number(base) === Number(top)) {
		if (!returnHtml) {
			return basePrice.unit ? `₹ ${basePrice.value} ${basePrice.unit}` : `₹ ${basePrice.value}`;
		} else {
			return basePrice.unit
				? `₹ ${basePrice.value} <span className="!text-[12px] md:text-[14px] text-[#4A5565] leading-[20px]">${basePrice.unit}</span>`
				: `₹ ${basePrice.value}`;
		}
	}

	if (!returnHtml) {
		if (basePrice.unit === topPrice.unit) {
			return `₹ ${basePrice.value} - ${topPrice.value} ${basePrice.unit}`;
		} else {
			return `₹ ${basePrice.value} ${basePrice.unit} - ${topPrice.value} ${topPrice.unit}`;
		}
	} else {
		if (basePrice.unit === topPrice.unit) {
			return `₹ ${basePrice.value} - ₹ ${topPrice.value} <span className="!text-[12px] md:text-[14px] text-[#4A5565] leading-[20px]">${basePrice.unit}</span>`;
		} else {
			return `₹ ${basePrice.value} <span className="!text-[12px] md:text-[14px] text-[#4A5565] leading-[20px]">${basePrice.unit}</span> - ₹ ${topPrice.value} <span className="!text-[12px] md:text-[14px] text-[#4A5565] leading-[20px]">${topPrice.unit}</span>`;
		}
	}
};

// Format prices
export const formatPrice = (price: number, opts?: { includeUnit?: boolean, returnZeroOnZero?: boolean }): string | number => {
	if (!price) {
		return opts?.returnZeroOnZero ? 0 : "Price to be Announced";
	}

	const includeUnit = opts?.includeUnit !== undefined ? !!opts.includeUnit : true;

	if (price >= 10000000) {
		const crores = (Math.floor(price / 100000) / 100).toFixed(2);
		return includeUnit ? `₹ ${crores} Crore*` : `₹ ${crores}`;
	} else {
		const lakhs = (Math.floor(price / 1000) / 100).toFixed(2);
		return includeUnit ? `₹ ${lakhs} Lakh*` : `₹ ${lakhs}`;
	}
};


// Safe image URL resolver (shared)
export const safeImageUrl = (raw: any, defaultFolder = "") => {
	if (!raw) return `${process.env.NEXT_PUBLIC_CDN_URL}placeholder-car.webp`;
	const src = Array.isArray(raw) ? raw[0] : raw;
	if (typeof src !== "string") return `${process.env.NEXT_PUBLIC_CDN_URL}placeholder-car.webp`;

	if (src.startsWith("/") || src.startsWith("http")) return src;
	if (src.includes("/")) return "/" + src.replace(/^\/+/, "");
	if (/\.(webp|jpg|jpeg|png|gif|avif)$/i.test(src))
		return `/${defaultFolder}/${src}`;
	return `${process.env.NEXT_PUBLIC_CDN_URL}placeholder-car.webp`;
};

// Extract top 3-4 key specs from the new categorized specs format
export function getTopSpecs(categories: SpecCategory[] | { name?: string; value?: any }[] = []): { name: string; value: string }[] {

	// If input is empty, return three empty placeholders
	if (!Array.isArray(categories) || categories.length === 0) {
		return [{ name: "", value: "" }, { name: "", value: "" }, { name: "", value: "" }];
	}

	const first = categories[0] as any;

	// If the shape matches categorized specs: [{ category: string, specs: [{name, value}] }, ...]
	if (first && typeof first === 'object' && 'category' in first && Array.isArray(first.specs)) {
		const result: { name: string; value: string }[] = [];

		const targetSpecs = [
			{ key: "Mileage", category: "Fuel & Performance" },
			{ key: "Fuel Type", category: "Fuel & Performance" },
			{ key: "Displacement", category: "Engine & Transmission" }
		];

		for (const { key, category } of targetSpecs) {
			const cat = (categories as SpecCategory[]).find(c => c.category === category);
			if (!cat) continue;

			const spec = cat.specs.find(s => s.name === key);
			if (spec) {
				result.push({ name: spec.name, value: String(spec.value) });
			}

			if (result.length === 3) break;
		}

		while (result.length < 3) result.push({ name: "", value: "" });
		return result;
	}

	// Otherwise assume a flat specs array: [{ name, value }, ...]
	const flat = (categories as { name?: string; value?: any }[])
		.filter(Boolean)
		.map(s => ({ name: s.name ?? "", value: String(s.value ?? "") }));

	const priority = [
		'ARAI Mileage', 'Mileage', 'Fuel Type', 'Transmission Type', 'Transmission',
		'Max Power', 'Power', 'Max Torque', 'Torque', 'No. of Doors', 'Seats'
	];

	const scored: { name: string; value: string; score: number }[] = [];
	for (const spec of flat) {
		if (!spec.value) continue;
		const matchIndex = priority.findIndex(p => p.toLowerCase() === (spec.name || '').toLowerCase());
		const score = matchIndex === -1 ? 99 : matchIndex;
		scored.push({ name: spec.name, value: spec.value, score });
	}

	// Deduplicate by value
	const unique = scored.filter((item, index, self) => index === self.findIndex(s => s.value === item.value));

	const top = unique.sort((a, b) => a.score - b.score).slice(0, 3).map(s => ({ name: s.name, value: s.value }));

	while (top.length < 3) top.push({ name: "", value: "" });
	return top;
}

/**
 * Build a compact quick-specs string for a variant.
 * Prioritises categorized specs (variant.specs) then falls back to top-level fields.
 */
export function getQuickSpecsFromVariant(v: any): string {
	try {
		// If categorized specs present, reuse getTopSpecs to fetch name/value pairs
		if (v?.specs && Array.isArray(v.specs) && v.specs.length > 0) {
			const top = getTopSpecs(v.specs as any);
			const values = top.map(t => (t && t.value ? String(t.value).trim() : "")).filter(Boolean);
			if (values.length > 0) return values.slice(0, 3).join(' • ');
		}

		// Fallback: collect common fields
		const parts: string[] = [];
		if (v?.fuel_type) parts.push(String(v.fuel_type).trim());
		if (v?.transmission) parts.push(String(v.transmission).trim());
		if (v?.mileage) parts.push(String(v.mileage).trim());
		if (v?.engine) parts.push(String(v.engine).trim());

		// Try to find engine displacement (cc) from categorized specs if present
		if ((!parts || parts.length === 0) && v?.specs && Array.isArray(v.specs)) {
			try {
				for (const cat of v.specs) {
					for (const s of cat.specs || []) {
						const name = String(s.name || "").toLowerCase();
						const value = String(s.value || "").toLowerCase();
						if (name.includes('displacement') || value.includes('cc')) {
							// prefer raw value as-is (e.g., '998 cc')
							parts.unshift(String(s.value || '').trim());
							break;
						}
					}
					if (parts.length > 0) break;
				}
			} catch (e) { }
		}
		// normalize/clean
		const cleaned = parts.map(p => p.replace(/\s+/g, ' ').trim()).filter(Boolean);
		return cleaned.slice(0, 3).join(' • ');
	} catch (e) {
		return '';
	}
}



/**
 * @deprecated Use convertModelTags instead. This function is kept for backward compatibility.
 * 
 * Wrapper for convertModelTags that maintains the old API.
 * Converts template tags to actual values based on model data.
 * 
 * @param template - Template string with tags
 * @param model - Model data object
 * @param options - Options: { for?: 'faq'; fallback?: string }
 * @returns Processed string
 */
export function convertModelsTag(template: string, model: any, options?: { for?: 'faq'; fallback?: string }): string {
	// Map old options to new options format
	const newOptions = options?.fallback ? { fallback: options.fallback } : undefined;
	return convertModelTags(template, model, newOptions);
}

/**
 * Generate compare page title and intro paragraph for a list of variants
 * @param variants 
 * @returns 
 */
export function generateCompareIntro(variants: Variant[]): { pageTitle: string; introText: string | null } {
	const validVariants = (variants || []).filter(Boolean) as Variant[];
	const pageTitle = validVariants.length >= 2
		? formatCompareHeading(validVariants.map(v => `${v.brand?.name} ${v.model?.name}`))
		: "Car Comparison";

	if (!validVariants.length) return { pageTitle, introText: null };

	const buildSpecMap = (v: Variant) => {
		const map = new Map<string, string>();
		(v.specs || []).forEach((cat: any) => {
			(cat.specs || []).forEach((s: any) => {
				const key = (s.label || s.key || s.name || "").toString().trim();
				const value = (s.value !== undefined && s.value !== null) ? String(s.value) : ((s.valueNormalized !== undefined && s.valueNormalized !== null) ? String(s.valueNormalized) + (s.unit ? ` ${s.unit}` : '') : '');
				if (key) map.set(key.toLowerCase(), value);
			});
		});
		return map;
	};

	const findSpec = (map: Map<string, string>, keys: string[]) => {
		for (const [k, v] of map.entries()) {
			for (const key of keys) {
				if (k.includes(key.toLowerCase()) && v) return v;
			}
		}
		return "";
	};

	const isEV = (v: Variant) => {
		const ft = (v.fuel_type || "").toString().toLowerCase();
		if (ft && ft.includes("electric")) return true;
		const map = buildSpecMap(v);
		if (findSpec(map, ["battery", "motor", "ev range", "range"])) return ft.includes("electric") || !!findSpec(map, ["battery capacity", "battery"]);
		return false;
	};

	const namesShort = validVariants.map((v) => `${v.brand?.name ?? ''} ${v.model?.name ?? ''}`.trim());
	const specs = validVariants.map((v) => {
		const map = buildSpecMap(v);
		return {
			price: v.price ?? null,
			enginePower: findSpec(map, ['power', 'engine power', 'max power', 'bhp', 'hp', 'kw']),
			motorPower: findSpec(map, ['motor power', 'motor']),
			mileage: findSpec(map, ['mileage', 'fuel efficiency', 'kmpl', 'kmpl (arai)', 'km/l', 'city/highway']),
			range: findSpec(map, ['range', 'driving range', 'claimed range']),
			tank: findSpec(map, ['fuel tank', 'fuel tank capacity', 'fuel capacity', 'tank capacity']),
			battery: findSpec(map, ['battery capacity', 'battery (kwh)', 'battery']),
			torque: findSpec(map, ['torque']),
			ev: isEV(v),
		};
	});

	const A = specs[0];
	const B = specs[1] ?? null;
	const nameA = namesShort[0] ?? 'Model A';
	const nameB = namesShort[1] ?? (namesShort[0] ? namesShort[0] : 'Model B');

	// Helper phrases when a single spec is missing
	const missingPrice = (n: string) => `${n} comes in a cost-effective price range suitable for most buyers.`;
	const missingEngine = (n: string) => `${n} offers good performance for daily driving.`;
	const missingMileage = (n: string) => `${n} gives decent efficiency for everyday use.`;
	const missingTank = (n: string) => `${n} has a suitable tank setup for regular travel.`;
	const missingMotor = (n: string) => `${n} comes with a smooth-running electric motor.`;
	const missingRange = (n: string) => `${n} offers a good driving range for city travel.`;
	const missingBattery = (n: string) => `${n} comes with a reliable battery setup.`;

	// Price sentence
	const priceA = A.price ? formatPrice(Number(A.price)) : "";
	const priceB = B && B.price ? formatPrice(Number(B.price)) : "";

	let paragraph = `Confused between ${nameA} or ${nameB}? Compare the ${nameA} with the ${nameB} at MyCarJunction to know which car matches your driving needs and budget.`;

	if (!priceA && !priceB) {
		paragraph += ' Both models come in a budget-friendly range, making them suitable for most buyers.';
	} else if (priceA && priceB) {
		paragraph += ` The ${nameA} comes at a price of ${priceA}, while ${nameB} is available at ${priceB}, helping you compare both models based on your budget.`;
	} else if (priceA && !priceB) {
		paragraph += ` ${nameA} price range starts at ${priceA}, while ${nameB} comes in a cost-effective range for most buyers.`;
	} else if (!priceA && priceB) {
		paragraph += ` ${nameB} price range starts at ${priceB}, while ${nameA} comes in a cost-effective range for most buyers.`;
	}

	// Performance / Engine or EV motor
	const aIsEV = A.ev;
	const bIsEV = B ? B.ev : false;

	if (B) {
		// two-model comparison sentences
		if (aIsEV) {
			paragraph += ` ${nameA} is powered by an electric motor delivering ${A.motorPower || 'good motor power'}.`;
		} else {
			paragraph += ` In performance, ${A.enginePower ? `${nameA} delivers ${A.enginePower}` : missingEngine(nameA)}`;
		}

		if (bIsEV) {
			paragraph += ` whereas ${nameB} is powered by an electric motor delivering ${B.motorPower || 'good motor power'}.`;
		} else {
			paragraph += ` whereas ${B && B.enginePower ? `${nameB} produces ${B.enginePower}, giving you a clear power comparison.` : ` ${missingEngine(nameB)}`}`;
		}
	} else {
		// single model case
		if (aIsEV) paragraph += ` ${nameA} is powered by an electric motor delivering ${A.motorPower || 'good motor power'}.`;
		else paragraph += ` ${A.enginePower ? `${nameA} delivers ${A.enginePower}.` : missingEngine(nameA)}`;
	}

	// Efficiency / Mileage or EV range
	if (!B) {
		if (aIsEV) paragraph += ` ${nameA} offers a driving range of ${A.range || 'a good driving range for city travel'} on a full charge.`;
		else paragraph += ` ${A.mileage ? `${nameA} offers a mileage of ${A.mileage}.` : missingMileage(nameA)}`;
	} else {
		if (aIsEV && bIsEV) {
			const ARange = A.range || '';
			const BRange = B.range || '';
			if (!ARange && !BRange) paragraph += ` Both EVs offer a reliable city-friendly driving range.`;
			else paragraph += ` ${nameA} offers a driving range of ${ARange || 'a good driving range for city travel'}, while ${nameB} delivers ${BRange || 'a good driving range for city travel'}.`;
		} else if (aIsEV && !bIsEV) {
			paragraph += ` ${nameA} offers a driving range of ${A.range || 'a good driving range for city travel'} on a full charge, while ${nameB} offers ${B.mileage || 'decent efficiency for everyday use'}.`;
		} else if (!aIsEV && bIsEV) {
			paragraph += ` ${nameA} offers a mileage of ${A.mileage || 'N/A'}, while ${nameB} provides ${B.range || 'a reliable city-friendly driving range'}.`;
		} else {
			if (!A.mileage && !B.mileage) paragraph += ` Both models provide decent efficiency for everyday usage.`;
			else paragraph += ` ${nameA} offers a mileage of ${A.mileage || 'N/A'}, and ${nameB} provides ${B.mileage || 'N/A'}, making it easier to judge running costs.`;
		}
	}

	// Fuel tank / battery capacity
	if (!B) {
		if (aIsEV) paragraph += ` ${nameA} runs on an electric battery pack with a capacity of ${A.battery ? `${A.battery} kWh` : 'a reliable battery setup'}, helping you compare driving range easily.`;
		else paragraph += ` ${A.tank ? `The fuel tank capacity of ${nameA} is ${A.tank}.` : missingTank(nameA)}`;
	} else {
		if (aIsEV && bIsEV) {
			if (!A.battery && !B.battery) paragraph += ` Both EVs are equipped with a dependable battery setup for daily mobility.`;
			else paragraph += ` ${nameA} comes with a ${A.battery ? `${A.battery} kWh battery` : 'reliable battery setup'}, while ${nameB} includes ${B.battery ? `${B.battery} kWh` : 'a dependable battery setup'}, helping you compare driving range easily.`;
		} else if (aIsEV && !bIsEV) {
			paragraph += ` ${nameA} runs on an electric battery pack with a capacity of ${A.battery ? `${A.battery} kWh` : 'a reliable battery setup'}, while ${nameB} has a tank capacity of ${B.tank || 'N/A'}, helping you compare driving range easily.`;
		} else if (!aIsEV && bIsEV) {
			paragraph += ` ${nameA} has a fuel tank capacity of ${A.tank || 'N/A'}, and on the other hand, ${nameB} runs on an electric battery pack with a capacity of ${B.battery ? `${B.battery} kWh` : 'a reliable battery setup'}, helping you compare driving range easily.`;
		} else {
			if (!A.tank && !B.tank) paragraph += ` Both models come with an efficient tank setup suited for daily commutes.`;
			else paragraph += ` The fuel tank capacity of ${nameA} is ${A.tank || 'N/A'}, and on the other hand, ${nameB} comes with a tank capacity of ${B.tank || 'N/A'}, helping you compare driving range easily.`;
		}
	}

	paragraph += ` You can also compare their colour options, wheelbase, boot space, interiors, safety features, and overall comfort to choose the one that suits your lifestyle. MyCarJunction keeps your comparison simple and clear.`;

	return { pageTitle, introText: paragraph };
}

/**
 * Extracts common compare-friendly spec values from a variant object.
 * Returns normalized fields used by the FAQ generator.
 */
export function extractCompareSpecs(v: Variant | any | null | undefined) {
	if (!v) return {
		isEV: false,
		enginePower: '',
		motorPower: '',
		mileage: '',
		range: '',
		tank: '',
		torque: '',
		battery: '',
		chargingTime: '',
		price: null,
		name: '',
		engineCC: '',
		engineType: '',
		seatingCapacity: '',
		airbags: '',
		adasFeatures: [],
		motorType: '',
	};

	const map = new Map<string, string>();
	try {
		(v.specs || v.specSections || []).forEach((cat: any) => {
			(cat.specs || []).forEach((s: any) => {
				const key = (s.label || s.key || s.name || "").toString().trim().toLowerCase();
				const value = (s.value !== undefined && s.value !== null) ? String(s.value) : ((s.valueNormalized !== undefined && s.valueNormalized !== null) ? String(s.valueNormalized) : '');
				if (key) map.set(key, value);
			});
		});
	} catch (e) { }

	const find = (keys: string[]) => {
		for (const [k, vstr] of map.entries()) {
			for (const key of keys) {
				if (k.toLowerCase().trim() === key.toLowerCase().trim() && vstr) return vstr;
			}
		}
		return '';
	};

	const isEV = ((v.fuel_types || '').toString().toLowerCase().includes('electric')) || find(['battery', 'motor', 'ev range', 'range']) === 'true';

	const enginePower = find(['max power', 'power', 'bhp', 'hp']).trim() || '';
	const motorPower = find(['motor power', 'motor']).trim() || '';
	const mileage = find(['mileage', 'arai mileage', 'fuel efficiency', 'kmpl', 'km/l', 'city/highway']).trim() || '';
	const range = find(['range', 'driving range', 'claimed range']).trim() || '';
	const tank = find(['fuel tank', 'fuel tank capacity', 'fuel capacity', 'tank capacity']).trim() || '';
	const torque = find(['torque', 'max torque']).trim() || '';
	const battery = find(['battery capacity', 'battery (kwh)', 'battery']).trim() || '';
	const chargingTime = find(['charging time', 'fast charge time', 'charge time']).trim() || '';

	const engineCC = find(['engine displacement', 'displacement', 'engine cc']).trim() || '';
	const engineType = find(['engine type', 'fuel']).trim() || '';
	const seatingCapacity = find(['seating capacity', 'seats']).trim() || '';
	const airbags = find(['airbags', 'no. of airbags']).trim() || '';
	const motorType = find(['motor type']).trim() || v.motor_type || '';

	const transmissionType = find(['transmission type']).trim() || '';

	const topSpeed = find(['top speed']).trim() || '';
	const length = find(['length']).trim() || '';
	const width = find(['width']).trim() || '';
	const height = find(['height']).trim() || '';
	const wheelbase = find(['Wheel Base']).trim() || '';
	const groundClearance = find(['ground clearance', 'Ground Clearance Unladen']).trim() || '';
	const tyreSize = find(['tyre size']).trim() || '';
	const wheelSize = find(['wheel size']).trim() || '';
	const bootSpace = find(['boot space']).trim() || '';
	const infotainmentSystem = find(['touchscreen size', 'infotainment system', 'touchscreen']).trim() || '';
	const musicSystem = find(['music system', 'audio system', 'speakers']).trim() || '';
	const cameraType = find(['Rear Camera', 'camera', 'parking camera', '360 camera']).trim() || '';
	const chargingType = find(['Charger Type']).trim() || '';

	// sunroof type
	const sunroofLabels = ['Sunroof', 'Panoramic Sunroof']
	const sunroofType = sunroofLabels.map((label) => find([label]) === 'true' ? label?.toLowerCase() : '').filter((item) => !!item);

	// adas features
	const adasLabels = ['Autonomous Emergency Braking', 'Lane Departure Warning', 'Adaptive Cruise Control'];
	const adasFeatures = adasLabels.map((label) => find([label]) === 'true' ? label?.toLowerCase() : '').filter((item) => !!item);


	return {
		isEV,
		enginePower,
		motorPower,
		mileage,
		range,
		tank,
		torque,
		battery,
		chargingTime,
		price: v.price ?? null,
		name: `${v.brand?.name ?? ''} ${v.model?.name ?? ''}`.trim(),
		engineCC,
		engineType,
		seatingCapacity,
		airbags,
		adasFeatures,
		motorType,
		transmissionType,
		topSpeed,
		length,
		width,
		height,
		wheelbase,
		groundClearance,
		tyreSize,
		wheelSize,
		bootSpace,
		infotainmentSystem,
		musicSystem,
		cameraType,
		sunroofType,
		chargingType,
	};
}

// Generate a consistent compare URL by sorting the detailed slugs of the variants
export const generateCompareURLByDetailedSlugs = (arr: string[]) => {
	return [...arr].sort((a, b) => {
		const [brandA, ...modelA] = a.split("-");
		const [brandB, ...modelB] = b.split("-");

		// Compare brand
		if (brandA.toLowerCase() !== brandB.toLowerCase()) {
			return brandA.localeCompare(brandB);
		}

		// Same brand → compare model
		return modelA.join("-").localeCompare(modelB.join("-"));
	}).join('-vs-');
};

function isCarMatchingBrand(car: any, brandQuery: string): boolean {
	if (!car || !brandQuery) return false;
	const brands = brandQuery.split(',').map(b => b.trim()).filter(Boolean);
	return brands.some((b) => {
		const cleanTarget = b.toLowerCase();
		const slugTarget = generateSlug(cleanTarget);

		const brandSlug = (car.brand_slug || '').toString().trim().toLowerCase();
		if (brandSlug && (brandSlug === cleanTarget || brandSlug === slugTarget)) return true;

		const detailedSlug = (car.detailed_slug || '').toString().trim().toLowerCase();
		if (detailedSlug) {
			if (detailedSlug.startsWith(`${cleanTarget}-`) || detailedSlug.startsWith(`${slugTarget}-`)) {
				return true;
			}
		}

		return false;
	});
}

/**
 * Resolves the exact comparison pairs the "Compare {model} with Similar Cars"
 * section (PopularComparisons) renders for a model page — the same
 * fetch-then-fallback logic (popular comparisons API, then similarModels
 * fallback, then brand-competitor filler), so anything derived from it
 * (e.g. schema.org isSimilarTo) can match what's actually shown on the page.
 */
export async function getModelComparisonPairs(opts: {
	model?: any;
	similarModels?: any[];
	limit?: number;
	filterTags?: string;
	isPopular?: boolean;
	fetchOtherComparisons?: boolean;
	filters?: ComparisonListQueryParams;
}): Promise<any[]> {
	const {
		model,
		similarModels,
		limit = 3,
		filterTags = "",
		isPopular = false,
		fetchOtherComparisons = false,
		filters,
	} = opts;

	const { _id: comparisonModelId = "", status: comparisonModelStatus } = model || {};
	const is_upcoming = comparisonModelStatus === 'upcoming';

	let data = (await getPopularComparisons(
		limit,
		filterTags,
		isPopular,
		fetchOtherComparisons ? undefined : comparisonModelId,
		filters,
	)) ?? [];

	// add comparison with similar models
	if (!is_upcoming && comparisonModelId && !data?.length && similarModels?.length) {
		const formattedSimilarModels = similarModels
			?.slice(0, limit)
			.map((item) => ({
				base_price: item?.price_range?.base || 0,
				brand: item?.brand?.name || "",
				brand_slug: item?.brand?.slug || "",
				image: item?.model?.image || "",
				model: item?.model?.name || "",
				slug: item?.model?.slug,
				_id: item?._id,
				detailed_slug: item?.model?.detailed_slug,
			}));

		const currentModel = {
			base_price: model?.price_range?.base || 0,
			brand: model?.brand_name || "",
			brand_slug: model?.brand_slug || "",
			image: model?.featured_image || "",
			model: model?.name || "",
			detailed_slug: model?.detailed_slug,
			slug: model?.slug,
			_id: model?._id,
		};

		data = formattedSimilarModels.map((car) => ({
			cars: [currentModel, car],
			slug: generateCompareURLByDetailedSlugs([model?.detailed_slug, car?.detailed_slug]),
		}));
	}
	
	if (filters?.brand && data?.length) {
		const targetBrand = filters.brand;
		data = data.map((item: any) => {
			let updatedItem = item;
			if (Array.isArray(updatedItem['cars']) && updatedItem['cars'].length > 1) {
				const carsList = [...updatedItem['cars']];
				// find index of car that matches the target brand
				const targetIndex = carsList.findIndex((car) => isCarMatchingBrand(car, targetBrand));
				if (targetIndex > 0) {
					const [targetCar] = carsList.splice(targetIndex, 1);
					carsList.unshift(targetCar);
					updatedItem = {
						...updatedItem,
						['cars']: carsList,
					};
				}
			}
			return updatedItem;
		});
	}

	return data;
}

/**
 * Given the comparison pairs from getModelComparisonPairs and the current
 * model's own detailed_slug, extracts just "the other car" from each pair —
 * i.e. the actual similar/compared car shown alongside the current model in
 * each Compare card.
 */
export function extractComparedCars(pairs: any[], currentModelDetailedSlug?: string): any[] {
	return (pairs || [])
		.map((pair) => {
			const cars = Array.isArray(pair?.cars) ? pair.cars : Array.isArray(pair?.car_ids) ? pair.car_ids : [];
			if (cars.length < 2) return null;
			const other = currentModelDetailedSlug
				? cars.find((c: any) => c?.detailed_slug !== currentModelDetailedSlug) ?? cars[1]
				: cars[1];
			return other || null;
		})
		.filter(Boolean);
}

// Utility functions
export const generateSlug = (name: string) => {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9\s-]/g, '')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.trim();
};

// Capitalize first letter and lowercase the rest
export const ucfirst = (str: string) => {
	if (!str) return '';
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Validate if a given compare URL is correctly formatted and can be generated from the slugs
export const validateCompareURL = (
	url: string
): { isValid: boolean; validURL: string } => {
	const slugs = url.split("-vs-");

	const validURL = generateCompareURLByDetailedSlugs(slugs);

	return {
		isValid: validURL === url,
		validURL,
	};
};

// Check if a description is empty or contains only non-informative HTML
export const isEmptyDescription = (desc: string) => {
	if (!desc) return true;
	const trimmed = desc.trim();
	return trimmed === "" || trimmed === "<p><br></p>" || trimmed === "<p></p>" || trimmed === "<br>";
};

// Convert a string to title case (capitalize first letter of each word)
export const dateFormat = (dateStr: string) => {
	// this function will convert date to 6th April 2028 format
	const date = new Date(dateStr);
	const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
	return date.toLocaleDateString('en-IN', options);
};

// Returns true if dateStr is today or up to exactly one calendar month away.
// Uses calendar-month arithmetic (not a fixed 30-day window) and fails safe
// (false) on missing/invalid dates.
export const isLaunchWithinOneMonth = (dateStr?: string | null): boolean => {
	if (!dateStr) return false;
	const launchDate = new Date(dateStr);
	if (isNaN(launchDate.getTime())) return false;

	const today = new Date();
	const oneMonthFromToday = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

	return launchDate.getTime() >= today.getTime() && launchDate.getTime() <= oneMonthFromToday.getTime();
};

export const isBudgetSlug = (slug: string) => {
	return (
		/^cars-under-\d+-lakh$/.test(slug) ||
		/^cars-above-\d+-lakh$/.test(slug)
	);
};

export const isCategorySlug = (slug: string) => {
	return slug.endsWith("-cars") && !slug.includes("-seater-") && !isTransmissionSlug(slug);
};

export const isTransmissionSlug = (slug: string) => {
    const withoutDashCars = slug.replace(/-cars$/, "");
    return slug.endsWith("-cars") && !slug.includes("-seater-") && TRANSMISSIONS.some((item) => item?.slug === withoutDashCars);
};

export const isSeatingCapacitySlug = (slug: string) => {
	return /^\d+-seater-cars$/.test(slug);
};

export const getAvailableBudgetPriceRanges = async (additionalParams: any = {}) => {
	const results = await Promise.all(
		Object.entries(BUDGET_PAGE_PRICE_RANGES).map(async ([key, range]) => {
			try {
				const res = await getModels({
					min_price: range.min,
					max_price: range.max,
					limit: 1,
					is_upcoming: false,
					...additionalParams
				});
				if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
					return { key, range };
				}
			} catch (error) {
				console.error(`Error checking range ${key}:`, error);
			}
			return null;
		})
	);

	const available: any = {};

	// Maintain original order
	Object.keys(BUDGET_PAGE_PRICE_RANGES).forEach(key => {
		const found = results.find(r => r && r.key === key);
		if (found) {
			available[key] = found.range;
		}
	});

	return available;
};

export const getAvailableSeatingCapacity = async (additionalParams: any = {}) => {
	const results = await Promise.all(
		Object.entries(SEATING_CAPACITY).map(async ([key, value]) => {
			try {
				const res = await getModels({
					seating_capacity: value.value,
					limit: 1,
					is_upcoming: false,
					...additionalParams
				});
				if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
					return { key, value };
				}
			} catch (error) {
				console.error(`Error checking range ${key}:`, error);
			}
			return null;
		})
	);

	const available: any = {};

	// Maintain original order
	Object.keys(SEATING_CAPACITY).forEach(key => {
		const found = results.find(r => r && r.key === key);
		if (found) {
			available[key] = found.value;
		}
	});

	return available;
};

// Extracts most expensive and most affordable variant details from the price extremes API response
export const getListingDynamicValuesExtremes = (priceExtremesRes: any) => {

	const { data: priceExtremesData } = priceExtremesRes || {};

	const mostExpensivePrice = formatPrice(
		Number(priceExtremesData?.mostExpensive?.price),
		{ returnZeroOnZero: true },
	);
	const mostAffordablePrice = formatPrice(
		Number(priceExtremesData?.mostAffordable?.price),
		{ returnZeroOnZero: true },
	);

	const mostEconomicalPrice = formatPrice(Number(priceExtremesData?.highestMileage?.price), { returnZeroOnZero: true });

	const NonEconomicalPrice = formatPrice(Number(priceExtremesData?.lowestMileage?.price), { returnZeroOnZero: true });

	const mostExpensive =
		priceExtremesData?.mostExpensive?.model &&
		`${priceExtremesData.mostExpensive.model.brand_name} ${priceExtremesData.mostExpensive.model.name}`;

	const mostAffordable =
		priceExtremesData?.mostAffordable?.model &&
		`${priceExtremesData.mostAffordable.model.brand_name} ${priceExtremesData.mostAffordable.model.name}`;

	const mostEconomical =
		priceExtremesData?.highestMileage?.model &&
		`${priceExtremesData.highestMileage.model.brand_name} ${priceExtremesData.highestMileage.model.name}`;

	const NonEconomical =
		priceExtremesData?.lowestMileage?.model &&
		`${priceExtremesData.lowestMileage.model.brand_name} ${priceExtremesData.lowestMileage.model.name}`;

	const bodyTypeName = priceExtremesData?.bodyTypes?.join(", ") || "";
	const transmissions = priceExtremesData?.transmissions?.join(", ") || "";
	const fuelTypeName = priceExtremesData?.fuelTypes?.join(', ') || "";
	const allSlugs = priceExtremesData?.allSlugs?.join(", ") || "";
	// No space after the comma: this is consumed as an API query param (model_id), not display text.
	const modelIds = priceExtremesData?.allModelsIds?.join(",") || "";
	const year = yearString;
	const month = monthString;

	return {
		mostExpensive,
		mostAffordable,
		mostEconomical,
		NonEconomical,
		mostExpensivePrice,
		mostAffordablePrice,
		mostEconomicalPrice,
		NonEconomicalPrice,
		bodyTypeName,
		transmissions,
		fuelTypeName,
		allSlugs,
		modelIds,
		year,
		month,
		upcomingCount: priceExtremesData?.upcomingCount
	};
}

export const findAndReplaceTag = (
	content: any,
	variables?: Record<string, any>
): any => {
	if (!content || !variables) return content;

	// STRING
	if (typeof content === 'string') {
		let result = content;

		for (const [key, value] of Object.entries(variables)) {
			const braceRegex = new RegExp(`\\{${key}\\}`, 'g');
			const smartQuoteRegex = new RegExp(`“${key}”`, 'g');
			const isMissing = value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value));
			const replacement = isMissing ? '' : String(value);

			result = result
				.replace(braceRegex, replacement)
				.replace(smartQuoteRegex, replacement);
		}

		return result;
	}

	// ARRAY (FAQs)
	if (Array.isArray(content)) {
		return content.map(item =>
			findAndReplaceTag(item, variables)
		);
	}

	// OBJECT (preserve shape)
	if (typeof content === 'object' && content !== null) {
		const updated: Record<string, any> = {};

		for (const key of Object.keys(content)) {
			updated[key] = findAndReplaceTag(
				content[key],
				variables
			);
		}

		return updated;
	}

	// primitives
	return content;
};

export const year = () => {
	return new Date().getFullYear();
}

/** Builds matching TOC / heading anchor ids from heading text. */
export function toHeadingId(text: string): string {
	return text
		.replace(/<[^>]+>/g, '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '-');
}

export function processNewsContent(html: string, isMobile: boolean): string {
	if (!html) return '';

	const withLazyImages = html.replace(/<img([^>]*?)>/gi, (match, attrs) => {
		const loadAttr = 'loading="lazy"';
		const sizeAttr = !attrs.includes('width')
			? `width="${isMobile ? 345 : 800}" height="${isMobile ? 172 : 400}"`
			: '';

		return `<img${attrs} ${loadAttr} ${sizeAttr}>`.replace(/\s+>/g, '>');
	});

	// Inject id attributes on h2 so Table of Content anchors can scroll to them
	return withLazyImages.replace(
		/<h2(\b[^>]*)>([\s\S]*?)<\/h2>/gi,
		(match, attrs: string, content: string) => {
			if (/\bid\s*=/i.test(attrs)) return match;
			const id = toHeadingId(content);
			if (!id) return match;
			return `<h2${attrs} id="${id}">${content}</h2>`;
		}
	);
}

export const getAvailableFuelTypes = async (additionalParams: any = {}) => {
	const { fuel_type: currentFuel } = additionalParams;
	const results = await Promise.all(
		FUEL_TYPES.filter((fuelType) => fuelType !== currentFuel).map(async (fuelType) => {
			try {
				const res = await getModels({
					fuel_type: fuelType, // ✅ keep as-is (PascalCase)
					limit: 1,
					is_upcoming: false,
					...additionalParams
				});

				if (res?.data?.length) {
					return fuelType;
				}
			} catch (error) {
				console.error(`Error checking fuel type ${fuelType}:`, error);
			}
			return null;
		})
	);

	const availableFuelTypes = results.filter(Boolean) as string[];

	const fuelTypes = availableFuelTypes
		.filter((fuelType) => fuelType !== currentFuel) // ✅ exact match
		.map((fuelType) => ({
			label: fuelType,
			href: `/${fuelType.toLowerCase()}-cars`, // ✅ only here lowercase
			title: fuelType,
		}));

	return fuelTypes;
};

export const getAvailableBodyTypes = async (additionalParams: any = {}) => {
	const results = await Promise.all(
		BODY_TYPES.map(async (bodyType: any) => {
			try {
				const res = await getModels({
					page: 1,
					limit: 1,
					body_type: bodyType?.slug,
					is_upcoming: false,
					...additionalParams
				});

				if (res?.data?.length) {
					return bodyType;
				}
			} catch (error) {
				console.error(`Error checking body type ${bodyType}:`, error);
			}
			return null;
		})
	);

	const availableBodyTypes = results.filter(Boolean) as string[];

	const bodyTypes = availableBodyTypes.map((bodyType: any) => ({
		label: bodyType.name,
		href: `/${bodyType.slug}-cars`,
		title: bodyType.name,
		value: bodyType.slug
	}));

	return bodyTypes;
};

// get available brands by checking models in it
export const getAvailableBrands = async (additionalParams: any = {}) => {
	const brandsData = await getBrands({ page: 1, limit: -1, is_active: true })
	const results = await Promise.all(
		(brandsData?.data ?? []).map(async (brand: any) => {
			try {
				const res = await getModels({
					page: 1,
					limit: 1,
					brand: brand?.slug,
					is_upcoming: false,
					...additionalParams
				});

				if (res?.data?.length) {
					return brand;
				}
			} catch (error) {
				console.error(`Error checking brand ${brand}:`, error);
			}
			return null;
		})
	);

	const availableBrands = results.filter(Boolean) as string[];

	const brands = availableBrands.map((brand: any) => ({
		name: brand.name,
		slug: brand.slug
	}));

	return brands;
};

/**
 * Parses car filter parameters from search queries into standardized arrays and values.
 */
export function parseFilterParams(searchQueries: any) {
	// Next.js hands back a string[] instead of a string when a query key is repeated
	// (e.g. ?fuel_type=Electric&fuel_type=Petrol) — normalize to a single string first.
	const toSingle = (v: unknown): string | undefined =>
		Array.isArray(v) ? v[0] : (v as string | undefined);

	const {
		price: priceRangeQuery,
		body_type,
		fuel_type,
		transmission,
		seating_capacity: seatingCapacity,
		mileage,
		sort,
		range,
		brand,
		battery_capacity
	} = searchQueries || {};

	const priceRange = toSingle(priceRangeQuery)?.split("-") || [];
	const minPrice = priceRange[0] !== undefined ? Number(priceRange[0]) : undefined;
	const maxPrice = priceRange[1] !== undefined ? Number(priceRange[1]) : undefined;

	const bodyTypes = toSingle(body_type)?.split(",") || [];
	const fuelTypes = toSingle(fuel_type)?.split(",") || [];
	const transmissions = toSingle(transmission)?.split(",") || [];
	const brands = toSingle(brand)?.split(",") || [];
	const seatingCapacities = toSingle(seatingCapacity)?.split(",") || [];
	const mileages = toSingle(mileage)?.split(",") || [];
	const ranges = toSingle(range)?.split(",") || [];
	const batteryCapacities = toSingle(battery_capacity)?.split(",") || [];

	const isFilterApplied = !!(priceRangeQuery || body_type || fuel_type || transmission || seatingCapacity || mileage || brand || range || battery_capacity);

	return {
		priceRangeQuery,
		minPrice,
		maxPrice,
		bodyTypes,
		fuelTypes,
		transmissions,
		brands,
		seatingCapacities,
		mileages,
		ranges,
		batteryCapacities,
		sort,
		isFilterApplied
	};
}

export const formatDuration = (seconds: number): string => {
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = seconds % 60;

	if (h > 0) {
		return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
	}

	return `${m}:${s.toString().padStart(2, "0")}`;
};

/**
 * Excludes discontinued variants from a raw variant list — unless the parent
 * model itself is discontinued, in which case every one of its variants is
 * discontinued too and none should be filtered out. Used everywhere a
 * variant count or starting price is derived, so discontinued variants never
 * factor into either.
 */
export function filterActiveVariants(variants: any[], modelStatus?: string): any[] {
	const modelIsDiscontinued = modelStatus === "discontinued";
	return modelIsDiscontinued
		? (variants || [])
		: (variants || []).filter((v: any) => v.status !== "discontinued");
}

/**
 * Utility to format raw API variant objects into the VariantItem structure used by UI components (e.g. VariantsTable).
 */
export function formatVariantData(
	variants: any[],
	options: {
		brand?: string;
		model?: string;
		brandName?: string;
		modelName?: string;
		currentVariantId?: string | number | null;
		modelStatus?: string;
		modelImage?: string;
		modelDetailedSlug?: string;
		// The by-model variants API doesn't populate `model`, so callers pass the
		// page's model id — lead CTAs need it (per-model lead config, car_id).
		modelId?: string;
	} = {}
): VariantItem[] {
	const { brand = "", model = "", brandName = "", modelName = "", currentVariantId = null, modelStatus, modelImage = "", modelDetailedSlug = "", modelId = "" } = options;

	// A discontinued model marks every one of its variants as discontinued.
	// Otherwise (active/upcoming model), discontinued variants are hidden from the list.
	const modelIsDiscontinued = modelStatus === "discontinued";
	const source = filterActiveVariants(variants, modelStatus);

	return (source || []).map((v: any) => ({
		id: v._id,
		name: v.name,
		slug: v.slug ?? "",
		brandSlug: v.brand?.slug || brand || "",
		modelSlug: v.model?.slug || model || "",
		modelName: v.model?.name || modelName || "",
		modelId: v.model?._id || v.model_id || modelId || "",
		modelDetailedSlug: v.model?.detailed_slug || modelDetailedSlug || "",
		engine: (
			findSpecValue(v, "engine_displacement") ||
			findSpecValue(v, "displacement") ||
			findSpecValue(v, "max_power") ||
			""
		).trim(),
		// Prefer the authoritative `transmission` enum field over the CMS-editable
		// `transmission_type` spec text, which can go stale (e.g. all "Turbo"
		// variants showing "Manual" in specs despite being CVT/DCT/AMT).
		transmission: (v.transmission || findSpecValue(v, "transmission_type") || "").trim(),
		fuel: v.fuel_types?.[0] || "",
		mileage: (
			findSpecValue(v, "arai_mileage") ||
			findSpecValue(v, "mileage") ||
			findSpecValue(v, "range") ||
			""
		).trim(),
		price: formatPrice(v.price, { includeUnit: true }) + '',
		rawPrice: Number(v.price) || 0,
		fuel_types: Array.isArray(v.fuel_types) ? v.fuel_types : [],
		specSections: Array.isArray(v.specSections) ? v.specSections : [],
		isViewing: v._id === currentVariantId,
		isUpcoming: v.status === "upcoming",
		brandName: v.brand?.name || brandName || "",
		isDiscontinued: modelIsDiscontinued || v.status === "discontinued",
		is_sanity_done: !!v.is_sanity_done,
		battery_capacity: findSpecValue(v, "battery_capacity") || "",
		status: v.status || null,
		launch_date: v.launch_date || "",
		// The Variant schema has no image field of its own — variants only ever
		// show the parent model's photo. `v.model` is only populated on
		// single-variant-detail responses; the by-model list endpoint returns
		// raw variant docs, so `modelImage` (passed in by the caller) is the
		// only source for list pages.
		featured_image: v.featured_image || v.model?.featured_image || modelImage || "",
	}));
}

export const mapDealerCity = (city: { code: number; dealerCount: number; name: string; slug: string; _id: string }, brandName: string, brandSlug: string) => {
	return {
		testId: `dealer-${city.slug}`,
		title: `${city.dealerCount} ${brandName || ""} Dealers`,
		description: city.name,
		href: `/${brandSlug}/dealer-showrooms/${city.slug}`,
		count: city.dealerCount,
	};
};

export const mapDealer = (dealer: { _id: string; slug: string; display_name: string; address: string; phone_no: string[]; email: string[] }) => ({
    id: dealer._id,
    testId: `dealer-${dealer.slug}`,
    headingText: dealer.display_name,
    address: dealer.address,
    phone: dealer.phone_no?.join(", "),
    // email: dealer.email?.join(", "),
});

export const dealerBrandMap = (brand: {dealerCount: number, slug: string, name: string, image: string}, citySlug: string)=>{
	const showroomCount = brand.dealerCount;
	const getBrandImageSrc = (image: string) => {
		if (!image) return "";
		if (/^https?:\/\//i.test(image)) return image;
		return `${process.env.NEXT_PUBLIC_CDN_URL}${String(image).replace(/^\/+/, "")}`;
	};
	return {
		testId: `brand-showroom-${brand.slug}`,
		title: brand.name,
		description: typeof showroomCount === "number" ? `${showroomCount} showrooms near you` : "Showrooms near you",
		href: `/${brand.slug}/dealer-showrooms/${citySlug}`,
		src: getBrandImageSrc(brand.image),
		count: showroomCount,
	};
}
